import React from "react";
// ANT DESIGN COMPONENTS
import { Button, message } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode, faDownload } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// INTERFACES
import { AdapterCdoModbusInterface } from "../../../../../interfaces/AdapterCdoModbus.interface";

export default function ComponentUploadJson({
	onUploadJson,
}: {
	onUploadJson: (newValue: AdapterCdoModbusInterface) => void;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");

	// ************************************************ */
	// HELPERS **************************************** */
	const TEMPLATE_JSON: AdapterCdoModbusInterface = {
		slave_id: 1,
		polling_interval: 2,
		ln2_to_msg_map: [
			{
				key: {
					msg: {
						position: 6,
						msg_id: "41b088b5-659d-4206-8af1-8ec0934eb3fc",
						active: true,
					},
					bank_name: "AB",
					lift_name: "AB",
					bank_id: "5ab2d339-c4bb-4bad-8e9b-484b64d2faee",
					lift_id: "1c496aaa-00ff-40ab-8703-67dd908e9c79",
				},
				val: {
					message: {
						kind: "ReadCoilsRequest",
						address: 4,
						quantity: 5,
					},
				},
			},
		],
		msg_to_ln2_map: [
			{
				key_2: 1,
				key_1: {
					message: {
						kind: "ReadCoilsResponse",
						data: [true, false],
					},
				},
				val: {
					msg: {
						position: 3,
						msg_id: "62eb2166-b062-454a-bd17-a82d9039a395",
						active: true,
					},
					bank_name: "Bank 1",
					lift_name: "GGG",
					bank_id: "4796bd87-48c8-4873-945e-2cd04ca3a797",
					lift_id: "62ccd686-df4f-4dfe-b556-fb2b86d5d0cc",
				},
			},
		],
	};

	const isObject = (value: unknown): value is Record<string, any> =>
		typeof value === "object" && value !== null && !Array.isArray(value);

	const isValidFEMessage = (value: any): boolean =>
		isObject(value) &&
		isObject(value.msg) &&
		typeof value.msg.position === "number" &&
		typeof value.msg.msg_id === "string" &&
		typeof value.msg.active === "boolean" &&
		typeof value.bank_name === "string" &&
		typeof value.lift_name === "string" &&
		typeof value.bank_id === "string" &&
		typeof value.lift_id === "string";

	const isValidRequestMessage = (value: any): boolean => {
		if (
			!isObject(value) ||
			!isObject(value.message) ||
			typeof value.message.kind !== "string"
		) {
			return false;
		}

		const msg = value.message;

		switch (msg.kind) {
			case "ReadCoilsRequest":
			case "ReadDiscreteInputsRequest":
			case "ReadHoldingRegistersRequest":
			case "ReadInputRegistersRequest":
				return (
					typeof msg.address === "number" && typeof msg.quantity === "number"
				);

			case "ReadWriteMultipleRegistersRequest":
				return (
					typeof msg.read_address === "number" &&
					typeof msg.read_quantity === "number" &&
					typeof msg.write_address === "number" &&
					Array.isArray(msg.data) &&
					msg.data.every((item: unknown) => typeof item === "number")
				);

			case "WriteSingleCoilRequest":
				return (
					typeof msg.address === "number" && typeof msg.value === "boolean"
				);

			case "WriteMultipleCoilRequests":
				return (
					typeof msg.address === "number" &&
					Array.isArray(msg.data) &&
					msg.data.every((item: unknown) => typeof item === "boolean")
				);

			case "WriteSingleRegisterRequest":
				return typeof msg.address === "number" && typeof msg.value === "number";

			case "WriteMultipleRegistersRequest":
				return (
					typeof msg.address === "number" &&
					Array.isArray(msg.data) &&
					msg.data.every((item: unknown) => typeof item === "number")
				);

			case "ReportServerIdRequest":
				return true;

			case "MaskWriteRegisterRequest":
				return (
					typeof msg.address === "number" &&
					typeof msg.and_mask === "number" &&
					typeof msg.or_mask === "number"
				);

			case "CustomRequest":
				return (
					typeof msg.function_code === "number" &&
					Array.isArray(msg.data) &&
					msg.data.every((item: unknown) => typeof item === "number")
				);

			default:
				return false;
		}
	};

	const isValidResponseMessage = (value: any): boolean => {
		if (
			!isObject(value) ||
			!isObject(value.message) ||
			typeof value.message.kind !== "string"
		) {
			return false;
		}

		const msg = value.message;

		switch (msg.kind) {
			case "ReadCoilsResponse":
			case "ReadDiscreteInputsResponse":
				return (
					Array.isArray(msg.data) &&
					msg.data.every((item: unknown) => typeof item === "boolean")
				);

			case "ReadHoldingRegistersResponse":
			case "ReadInputRegistersResponse":
				return (
					Array.isArray(msg.data) &&
					msg.data.every((item: unknown) => typeof item === "number")
				);

			case "ReadWriteMultipleRegistersResponse":
				return (
					Array.isArray(msg.read_data) &&
					msg.read_data.every((item: unknown) => typeof item === "number")
				);

			case "WriteSingleCoilResponse":
				return (
					typeof msg.address === "number" && typeof msg.value === "boolean"
				);

			case "WriteMultipleCoilsResponse":
			case "WriteMultipleRegistersResponse":
				return typeof msg.address === "number" && typeof msg.count === "number";

			case "WriteSingleRegisterResponse":
				return typeof msg.address === "number" && typeof msg.value === "number";

			case "ReportServerIdResponse":
				return (
					typeof msg.server_id === "number" &&
					typeof msg.running === "boolean" &&
					Array.isArray(msg.additional_data) &&
					msg.additional_data.every((item: unknown) => typeof item === "number")
				);

			case "MaskWriteRegisterResponse":
				return (
					typeof msg.address === "number" &&
					typeof msg.and_mask === "number" &&
					typeof msg.or_mask === "number"
				);

			case "CustomResponse":
				return (
					typeof msg.function_code === "number" &&
					Array.isArray(msg.data) &&
					msg.data.every((item: unknown) => typeof item === "number")
				);

			default:
				return false;
		}
	};

	const isValidLn2ToMsgEntry = (item: any): boolean =>
		isObject(item) &&
		isValidFEMessage(item.key) &&
		isValidRequestMessage(item.val);

	const isValidMsgToLn2Entry = (item: any): boolean =>
		isObject(item) &&
		typeof item.key_2 === "number" &&
		isValidResponseMessage(item.key_1) &&
		isValidFEMessage(item.val);

	const sanitizeJson = (data: any): AdapterCdoModbusInterface => {
		const validLn2ToMsgMap = Array.isArray(data?.ln2_to_msg_map)
			? data.ln2_to_msg_map.filter(isValidLn2ToMsgEntry)
			: [];

		const validMsgToLn2Map = Array.isArray(data?.msg_to_ln2_map)
			? data.msg_to_ln2_map.filter(isValidMsgToLn2Entry)
			: [];

		return {
			slave_id: typeof data?.slave_id === "number" ? data.slave_id : 1,
			polling_interval:
				typeof data?.polling_interval === "string" ||
				typeof data?.polling_interval === "number"
					? data.polling_interval
					: "2",
			ln2_to_msg_map: validLn2ToMsgMap,
			msg_to_ln2_map: validMsgToLn2Map,
		};
	};

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickDownloadTemplate = () => {
		const blob = new Blob([JSON.stringify(TEMPLATE_JSON, null, 4)], {
			type: "application/json",
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "modbus-template.json";
		a.click();
		URL.revokeObjectURL(url);
	};

	const uploadJSONFile = (onLoadCallback: (data: any) => void) => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "application/json";

		input.onchange = (event: any) => {
			const file = event.target.files?.[0];
			if (!file) return;

			const reader = new FileReader();

			reader.onload = (e) => {
				try {
					const text = e.target?.result as string;
					const data = JSON.parse(text);
					onLoadCallback(data);
				} catch (error) {
					message.error("Invalid JSON file");
				} finally {
					input.value = "";
				}
			};

			reader.readAsText(file);
		};

		input.click();
	};

	const onClickUploadJsonFile = () => {
		uploadJSONFile((data) => {
			const sanitized = sanitizeJson(data);

			const originalLn2ToMsg = Array.isArray(data?.ln2_to_msg_map)
				? data.ln2_to_msg_map.length
				: 0;
			const originalMsgToLn2 = Array.isArray(data?.msg_to_ln2_map)
				? data.msg_to_ln2_map.length
				: 0;

			const validLn2ToMsg = sanitized.ln2_to_msg_map.length;
			const validMsgToLn2 = sanitized.msg_to_ln2_map.length;

			onUploadJson(sanitized);

			message.success(
				`JSON uploaded successfully. ln2_to_msg_map: ${validLn2ToMsg}/${originalLn2ToMsg}, msg_to_ln2_map: ${validMsgToLn2}/${originalMsgToLn2}`,
			);
		});
	};

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<div className='col-md-12 mt-3'>
				<div className='generalStyles__flex'>
					<div className='generalStyles__inputFlexNameLarge' />
					<div className='generalStyles__width100'>
						<Button
							className='buttonStyle__28 generalStyles__width100'
							onClick={onClickDownloadTemplate}
						>
							<FontAwesomeIcon
								icon={faDownload}
								className='generalStyles__mrFix'
							/>
							{t("general.downloadTemplate")}
						</Button>
					</div>
					<div className='generalStyles__infoTooltipIconBlank' />
				</div>
			</div>

			<div className='col-md-12 mt-3'>
				<div className='generalStyles__flex'>
					<div className='generalStyles__inputFlexNameLarge' />
					<div className='generalStyles__width100'>
						<Button
							className='buttonStyle__28 generalStyles__width100'
							onClick={onClickUploadJsonFile}
						>
							<FontAwesomeIcon icon={faCode} className='generalStyles__mrFix' />
							{t("general.uploadJson")}
						</Button>
					</div>
					<div className='generalStyles__infoTooltipIconBlank' />
				</div>
			</div>
		</div>
	);
}
